import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Box,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CustomSwitch from "global/components/CustomSwitch/CustomSwitch";
import CustomInput from "global/components/CustomInput/CustomInput";
import {
  getConfigurationData,
  handleEnableConfigUpdateBtn,
  handleUpdate,
  configurationSkeleton,
} from "../MySignRequestHelper";
import {
  getDefaultDateFormat,
  updateDefaultDateFormatConfig,
} from "../MySignRequestService";
import {
  handleErrorMessage,
  javaToMomentFormat,
  momentToJavaFormat,
} from "helpers/methods";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import { DateRange } from "models/interfaces";
import { selectEmail } from "redux/authSlice";
import { useAppSelector } from "hooks/use-app-select";
import { useAppDispatch } from "redux/hooks";
import {
  selectDefaultDateFormat,
  updateDefaultDateFormat,
} from "redux/preferencesSlice";
import { isTruthy } from "helpers/methods";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import strings from "global/constants/StringConstants";
import { useFeatureState, FeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureBanner from "global/components/FeatureBanner/FeatureBanner";

const DATE_FORMAT_OPTIONS = [
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD MMM YYYY",
  "MMM DD, YYYY",
];

const MySignRequestsConfigurations = () => {
  useTitle(title.CONFIGURATION);
  const userEmail: string = useAppSelector(selectEmail);
  const appDispatch = useAppDispatch();
  const defaultDateFormat = useAppSelector(selectDefaultDateFormat);

  const reminderState = useFeatureState(FeatureKeys.REMINDER_AUTOMATIC);
  const delegationState = useFeatureState(FeatureKeys.ENVELOPE_DELEGATION);
  const autoExpirationState = useFeatureState(
    FeatureKeys.ENVELOPE_AUTO_EXPIRATION,
  );
  const isReminderAvailable =
    reminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isDelegationAvailable =
    delegationState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isAutoExpirationAvailable =
    autoExpirationState.status === strings.FEATURE_STATUS_AVAILABLE;

  const getFeatureTooltip = (state: FeatureState): string => {
    if (state.status === strings.FEATURE_STATUS_NOT_IN_PLAN)
      return strings.FEATURE_GATE_UPSELL_TOOLTIP;
    if (state.status === strings.FEATURE_STATUS_ADMIN_DISABLED)
      return (
        state.disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP
      );
    return "";
  };

  const [loading, setLoading] = useState(false);
  const [openDateRangePicker, setOpenDateRangePicker] =
    useState<boolean>(false);

  const [configurationFields, setConfigurationFields] = useState<any>({
    autoReminder: false,
    daysBeforeFirstReminder: 0,
    daysBetweenReminders: 0,
    expirationDays: 0,
    allowSignatureDelegation: false,
    delegateName: "",
    delegateEmail: "",
    delegateStartDate: "",
    delegateEndDate: "",
  });

  const [previousValues, setPreviousValues] = useState<any>({
    autoReminder: false,
    daysBeforeFirstReminder: 0,
    daysBetweenReminders: 0,
    expirationDays: 0,
    allowSignatureDelegation: false,
    delegateName: "",
    delegateEmail: "",
    delegateStartDate: "",
    delegateEndDate: "",
  });

  const [updateButtonEnabled, setUpdateButtonEnabled] = useState({
    btn1: false,
    btn2: false,
    btn3: false,
  });

  const [errors, setErrors] = useState<any>({
    daysBeforeFirstReminder: "",
    daysBetweenReminders: "",
  });

  const [autoExpErrors, setAutoExpErrors] = useState<any>({
    expirationDays: "",
  });

  const [autoDelegateErrors, setAutoDelegateErrors] = useState<any>({
    delegationEmail: "",
    delegationName: "",
    delegationDateRange: "",
  });

  useEffect(() => {
    if (userEmail) {
      getConfigurationData(
        setLoading,
        setConfigurationFields,
        setPreviousValues,
        userEmail,
      );
    }
  }, [userEmail]);

  const [localDateFormat, setLocalDateFormat] =
    useState<string>(defaultDateFormat);
  const [dateFormatUpdating, setDateFormatUpdating] = useState<boolean>(false);
  const isDateFormatDirty = localDateFormat !== defaultDateFormat;

  useEffect(() => {
    const fetchDateFormat = async () => {
      try {
        const response = await getDefaultDateFormat();
        // Endpoint returns a bare JSON string like "MM/dd/yyyy" (Java tokens).
        // Some HTTP layers may unwrap to an object, so probe common shapes too.
        const raw =
          typeof response === "string"
            ? response
            : (response?.value ??
              response?.data?.value ??
              response?.dateFormat);
        if (raw && typeof raw === "string") {
          const moment = javaToMomentFormat(raw);
          appDispatch(updateDefaultDateFormat(moment));
          setLocalDateFormat(moment);
        }
      } catch (err) {
        // Silent fail — keep the locally cached default
      }
    };
    fetchDateFormat();
  }, []);

  useEffect(() => {
    setLocalDateFormat(defaultDateFormat);
  }, [defaultDateFormat]);

  const handleDateFormatUpdate = async () => {
    if (!isDateFormatDirty) return;
    setDateFormatUpdating(true);
    setLoading(true);
    try {
      await updateDefaultDateFormatConfig(momentToJavaFormat(localDateFormat));
      appDispatch(updateDefaultDateFormat(localDateFormat));
    } catch (err: any) {
      handleErrorMessage(err);
    } finally {
      setDateFormatUpdating(false);
      setLoading(false);
    }
  };

  useEffect(() => {}, [previousValues]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    const isString = isNaN(Number(value));
    let valueByType = isString ? value : value.replace(/[^0-9]/g, "");

    if (name === "expirationPassword" && parseInt(valueByType) > 365) {
      return;
    }
    setConfigurationFields((prev: any) => ({
      ...prev,
      [name]: valueByType,
    }));
    handleEnableConfigUpdateBtn(
      name,
      valueByType,
      setUpdateButtonEnabled,
      updateButtonEnabled,
      previousValues,
    );

    if (name === "delegateName") {
      setAutoDelegateErrors((prev: any) => ({
        ...prev,
        delegationName: isTruthy(String(valueByType).trim())
          ? ""
          : "Please enter name.",
      }));
    }

    if (name === "delegateEmail") {
      const trimmedEmail = String(valueByType).trim();
      setAutoDelegateErrors((prev: any) => ({
        ...prev,
        delegationEmail: !isTruthy(trimmedEmail)
          ? "Please enter a email."
          : trimmedEmail.includes(",")
            ? "Please enter a single valid email"
            : strings.regex.test(trimmedEmail)
              ? ""
              : "Please enter a valid email",
      }));
    }
  };

  const handleOnCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;
    setConfigurationFields((prev: any) => ({
      ...prev,
      [name]: checked,
    }));
    handleEnableConfigUpdateBtn(
      name,
      checked,
      setUpdateButtonEnabled,
      updateButtonEnabled,
      previousValues,
    );
  };

  if (loading) {
    return configurationSkeleton();
  }

  const delegateEmailTrimmed = (configurationFields.delegateEmail || "").trim();
  const effectiveAutoReminder =
    isReminderAvailable && configurationFields.autoReminder;
  const effectiveAllowDelegation =
    isDelegationAvailable && configurationFields.allowSignatureDelegation;
  const isAutoDelegationInvalid =
    effectiveAllowDelegation &&
    (!isTruthy((configurationFields.delegateName || "").trim()) ||
      !isTruthy(delegateEmailTrimmed) ||
      delegateEmailTrimmed.includes(",") ||
      !strings.regex.test(delegateEmailTrimmed) ||
      !isTruthy(configurationFields.delegateStartDate) ||
      !isTruthy(configurationFields.delegateEndDate));
  const reminderTooltip = getFeatureTooltip(reminderState);
  const delegationTooltip = getFeatureTooltip(delegationState);
  const autoExpirationTooltip = getFeatureTooltip(autoExpirationState);

  return (
    <Box
      sx={{
        pt: "10px",
        width: "100%",
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2,minmax(0,1fr))" },
        alignItems: "stretch",
      }}
    >
      <Box sx={{ display: "flex" }}>
        <Card
          variant="outlined"
          sx={{
            border: "none",
            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.1)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ p: 3, flex: 1 }}>
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                display="flex"
                alignItems="center"
              >
                <Typography variant="h6" sx={{ textWrap: "nowrap" }} mr={2}>
                  Automated Email Reminder
                </Typography>
                <Tooltip
                  title={reminderTooltip}
                  disableHoverListener={isReminderAvailable}
                  arrow
                >
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <CustomSwitch
                      checked={effectiveAutoReminder}
                      onChange={handleOnCheck}
                      name="autoReminder"
                      aria-label="Automated Email Reminder"
                      disabled={!isReminderAvailable}
                    />
                    {!isReminderAvailable && (
                      <LockOutlinedIcon
                        sx={{ fontSize: 16, color: "text.disabled", ml: 0.5 }}
                      />
                    )}
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={1}>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 3, lineHeight: 1.6 }}
                >
                  Set up automated email reminders for your signers to complete
                  the signing process and add deadline notifications to expire
                  untouched requests.
                </Typography>
              </Grid>
              {!isReminderAvailable && (
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mb={2}>
                  <FeatureBanner
                    state={reminderState}
                    featureName="Automated reminders"
                    variant="compact"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid container direction="row" spacing={2} alignItems="center">
                  <Grid item>
                    <InputLabel htmlFor="daysBeforeFirstReminder">
                      Set first reminder in{" "}
                    </InputLabel>
                  </Grid>
                  <Grid item>
                    <CustomInput
                      disabled={!effectiveAutoReminder}
                      required
                      id="daysBeforeFirstReminder"
                      type="text"
                      name="daysBeforeFirstReminder"
                      onChange={handleOnChange}
                      propsToInputElement={{ minLength: 1, maxLength: 3 }}
                      value={configurationFields.daysBeforeFirstReminder}
                      sx={{
                        width: "60px",
                        "& .MuiOutlinedInput-input": {
                          padding: "7.5px 14px",
                        },
                      }}
                      error={
                        configurationFields.daysBeforeFirstReminder <= 0 &&
                        !!errors.daysBeforeFirstReminder
                      }
                    />
                  </Grid>
                  <Grid item>
                    <InputLabel>days</InputLabel>
                  </Grid>
                </Grid>
                {configurationFields.daysBeforeFirstReminder <= 0 && (
                  <FormHelperText error role="alert">
                    {errors.daysBeforeFirstReminder}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={1}>
                <Grid container direction="row" spacing={2} alignItems="center">
                  <Grid item>
                    <InputLabel htmlFor="daysBetweenReminders">
                      Repeat reminder after
                    </InputLabel>
                  </Grid>
                  <Grid item>
                    <CustomInput
                      required
                      disabled={!effectiveAutoReminder}
                      id="daysBetweenReminders"
                      type="text"
                      name="daysBetweenReminders"
                      onChange={handleOnChange}
                      value={configurationFields.daysBetweenReminders}
                      propsToInputElement={{ minLength: 1, maxLength: 3 }}
                      sx={{
                        width: "60px",
                        "& .MuiOutlinedInput-input": {
                          padding: "7.5px 14px",
                        },
                      }}
                      error={
                        configurationFields.daysBetweenReminders <= 0 &&
                        !!errors.daysBetweenReminders
                      }
                    />
                  </Grid>
                  <Grid item>
                    <InputLabel>days </InputLabel>
                  </Grid>
                </Grid>
                {configurationFields.daysBetweenReminders <= 0 && (
                  <FormHelperText error role="alert">
                    {errors.daysBetweenReminders}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={2}>
                <Tooltip
                  title={reminderTooltip}
                  disableHoverListener={isReminderAvailable}
                  arrow
                >
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <Button
                      id="update_reminder_btn"
                      size="small"
                      variant="contained"
                      disabled={
                        !updateButtonEnabled.btn1 || !isReminderAvailable
                      }
                      endIcon={
                        !isReminderAvailable ? (
                          <LockOutlinedIcon sx={{ fontSize: 16 }} />
                        ) : undefined
                      }
                      onClick={() =>
                        handleUpdate(
                          "autoEmailReminder",
                          setLoading,
                          setPreviousValues,
                          errors,
                          setErrors,
                          autoExpErrors,
                          setAutoExpErrors,
                          configurationFields,
                          setUpdateButtonEnabled,
                          updateButtonEnabled,
                          autoDelegateErrors,
                          setAutoDelegateErrors,
                          userEmail,
                        )
                      }
                    >
                      Update
                    </Button>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
      <Box sx={{ display: "flex" }}>
        <Card
          variant="outlined"
          sx={{
            border: "none",
            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.1)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ p: 3, flex: 1 }}>
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                display="flex"
                alignItems="center"
              >
                <Typography variant="h6" sx={{ mr: 1 }}>
                  Auto Expiration
                </Typography>
                <Tooltip
                  title={autoExpirationTooltip}
                  disableHoverListener={isAutoExpirationAvailable}
                  arrow
                >
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    {!isAutoExpirationAvailable && (
                      <LockOutlinedIcon
                        sx={{ fontSize: 18, color: "text.disabled" }}
                      />
                    )}
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={2}>
                <Typography variant="body2">
                  This setting will be applied when creating all new requests
                  and templates. Set the auto-expiration period to 6 months.
                </Typography>
              </Grid>
              {!isAutoExpirationAvailable && (
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={2}>
                  <FeatureBanner
                    state={autoExpirationState}
                    featureName="Auto expiration"
                    variant="compact"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={2}>
                <Tooltip
                  title={autoExpirationTooltip}
                  disableHoverListener={isAutoExpirationAvailable}
                  arrow
                >
                  <Box component="span" sx={{ display: "inline-block" }}>
                    <Grid
                      container
                      direction="row"
                      spacing={2}
                      alignItems="center"
                    >
                      <Grid item>
                        <InputLabel htmlFor="expirationDays">
                          Sent request expires in
                        </InputLabel>
                      </Grid>
                      <Grid item>
                        <CustomInput
                          required
                          id="expirationDays"
                          type="text"
                          name="expirationDays"
                          onChange={handleOnChange}
                          value={configurationFields.expirationDays}
                          disabled={!isAutoExpirationAvailable}
                          propsToInputElement={{ minLength: 1, maxLength: 3 }}
                          sx={{
                            width: "60px",
                            "& .MuiOutlinedInput-input": {
                              padding: "7.5px 14px",
                            },
                          }}
                          error={
                            (configurationFields.expirationDays > 180 ||
                              configurationFields.expirationDays <= 0) &&
                            !!autoExpErrors.expirationDays
                          }
                        />
                      </Grid>
                      <Grid item>
                        <InputLabel>days </InputLabel>
                      </Grid>
                    </Grid>
                  </Box>
                </Tooltip>
                {(configurationFields.expirationDays > 180 ||
                  configurationFields.expirationDays <= 0) && (
                  <FormHelperText error role="alert">
                    {autoExpErrors.expirationDays}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} mt={2}>
                <Tooltip
                  title={autoExpirationTooltip}
                  disableHoverListener={isAutoExpirationAvailable}
                  arrow
                >
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <Button
                      id="update_expiry_btn"
                      size="small"
                      variant="contained"
                      disabled={
                        !updateButtonEnabled.btn2 || !isAutoExpirationAvailable
                      }
                      endIcon={
                        !isAutoExpirationAvailable ? (
                          <LockOutlinedIcon sx={{ fontSize: 16 }} />
                        ) : undefined
                      }
                      onClick={() =>
                        handleUpdate(
                          "autoExpirations",
                          setLoading,
                          setPreviousValues,
                          errors,
                          setErrors,
                          autoExpErrors,
                          setAutoExpErrors,
                          configurationFields,
                          setUpdateButtonEnabled,
                          updateButtonEnabled,
                          autoDelegateErrors,
                          setAutoDelegateErrors,
                          userEmail,
                        )
                      }
                    >
                      Update
                    </Button>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
      <Box sx={{ display: "flex" }}>
        <Card
          variant="outlined"
          sx={{
            border: "none",
            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.1)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ p: 3, flex: 1 }}>
            <Grid container>
              <Grid item xs={12} display="flex" alignItems="center">
                <Typography variant="h6" sx={{ textWrap: "nowrap" }} mr={2}>
                  Automated Delegation
                </Typography>
                <Tooltip
                  title={delegationTooltip}
                  disableHoverListener={isDelegationAvailable}
                  arrow
                >
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <CustomSwitch
                      checked={effectiveAllowDelegation}
                      onChange={handleOnCheck}
                      name="allowSignatureDelegation"
                      aria-label="Automated Delegation"
                      disabled={!isDelegationAvailable}
                    />
                    {!isDelegationAvailable && (
                      <LockOutlinedIcon
                        sx={{ fontSize: 16, color: "text.disabled", ml: 0.5 }}
                      />
                    )}
                  </Box>
                </Tooltip>
              </Grid>

              <Grid item xs={12} mt={1}>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 3, lineHeight: 1.6 }}
                >
                  Set up automated delegation for your signers to forward the
                  signing process and let others sign on their behalf.
                </Typography>
              </Grid>
              {!isDelegationAvailable && (
                <Grid item xs={12} mt={1}>
                  <FeatureBanner
                    state={delegationState}
                    featureName="Automated delegation"
                    variant="compact"
                  />
                </Grid>
              )}

              <Grid item xs={12} mt={1}>
                <Grid container direction="row" spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <InputLabel htmlFor="delegateName">Delegate Name</InputLabel>
                    <CustomInput
                      disabled={!effectiveAllowDelegation}
                      required
                      id="delegateName"
                      type="text"
                      name="delegateName"
                      onChange={handleOnChange}
                      value={configurationFields.delegateName}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-input": {
                          padding: "7.5px 14px",
                        },
                      }}
                      error={!!autoDelegateErrors?.delegationName}
                    />
                    {!isTruthy(configurationFields.delegateName) &&
                      autoDelegateErrors.delegationName && (
                        <FormHelperText error role="alert">
                          {autoDelegateErrors.delegationName}
                        </FormHelperText>
                      )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputLabel htmlFor="delegateEmail">
                      Delegate Email
                    </InputLabel>
                    <CustomInput
                      disabled={!effectiveAllowDelegation}
                      required
                      id="delegateEmail"
                      type="email"
                      name="delegateEmail"
                      onChange={handleOnChange}
                      value={configurationFields.delegateEmail}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-input": {
                          padding: "7.5px 14px",
                        },
                      }}
                      error={!!autoDelegateErrors?.delegationEmail}
                    />
                    {autoDelegateErrors?.delegationEmail && (
                      <FormHelperText error role="alert">
                        {autoDelegateErrors?.delegationEmail}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <InputLabel sx={{ mb: 0.5 }}>Delegation date range</InputLabel>
                <Box
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      minHeight: "40px !important",
                      height: "40px !important",
                      border: "1px solid",
                      borderColor: configurationFields.allowSignatureDelegation
                        ? "divider"
                        : "action.disabledBackground",
                      borderRadius: 1,
                      backgroundColor:
                        configurationFields.allowSignatureDelegation
                          ? "transparent"
                          : "action.disabledBackground",
                      transition: "border-color 0.2s",
                    },
                    "& .MuiOutlinedInput-root:hover":
                      configurationFields.allowSignatureDelegation
                        ? { borderColor: "text.secondary" }
                        : {},
                    "& .MuiOutlinedInput-input": {
                      padding: "7.5px 8px",
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  <CustomDateRangePicker
                    disableFuture={false}
                    minDate={new Date().toISOString().split("T")[0]}
                    dateRange={{
                      fromDate: configurationFields.delegateStartDate,
                      toDate: configurationFields.delegateEndDate,
                    }}
                    openDateRangePicker={openDateRangePicker}
                    setOpenDateRangePicker={setOpenDateRangePicker}
                    setDateRange={(dateRange: DateRange) => {
                      setConfigurationFields((prev: any) => ({
                        ...prev,
                        delegateStartDate: dateRange.fromDate,
                        delegateEndDate: dateRange.toDate,
                      }));
                      handleEnableConfigUpdateBtn(
                        "delegateStartDate",
                        dateRange.fromDate?.toString() || "",
                        setUpdateButtonEnabled,
                        updateButtonEnabled,
                        previousValues,
                      );
                      handleEnableConfigUpdateBtn(
                        "delegateEndDate",
                        dateRange.toDate?.toString() || "",
                        setUpdateButtonEnabled,
                        updateButtonEnabled,
                        previousValues,
                      );
                    }}
                    disabled={!effectiveAllowDelegation}
                  />
                </Box>
                {!isTruthy(configurationFields.delegateStartDate) &&
                  !isTruthy(configurationFields.delegateEndDate) &&
                  autoDelegateErrors.delegationDateRange && (
                    <FormHelperText error role="alert">
                      {autoDelegateErrors.delegationDateRange}
                    </FormHelperText>
                  )}
              </Grid>

              <Grid item xs={12} mt={2}>
                <Tooltip
                  title={delegationTooltip}
                  disableHoverListener={isDelegationAvailable}
                  arrow
                >
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <Button
                      id="update_delegation_btn"
                      size="small"
                      variant="contained"
                      disabled={
                        !updateButtonEnabled.btn3 ||
                        isAutoDelegationInvalid ||
                        !isDelegationAvailable
                      }
                      endIcon={
                        !isDelegationAvailable ? (
                          <LockOutlinedIcon sx={{ fontSize: 16 }} />
                        ) : undefined
                      }
                      onClick={() =>
                        handleUpdate(
                          "autoDelegation",
                          setLoading,
                          setPreviousValues,
                          errors,
                          setErrors,
                          autoExpErrors,
                          setAutoExpErrors,
                          configurationFields,
                          setUpdateButtonEnabled,
                          updateButtonEnabled,
                          autoDelegateErrors,
                          setAutoDelegateErrors,
                          userEmail,
                        )
                      }
                    >
                      Update
                    </Button>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
      <Box sx={{ display: "flex" }}>
        <Card
          variant="outlined"
          sx={{
            border: "none",
            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.1)",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ p: 3, flex: 1 }}>
            <Grid container>
              <Grid item xs={12} display="flex" alignItems="center">
                <Typography variant="h6" sx={{ textWrap: "nowrap" }} mr={2}>
                  Default Date Format
                </Typography>
              </Grid>
              <Grid item xs={12} mt={1}>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 3, lineHeight: 1.6 }}
                >
                  Choose the format used by default whenever a Date or Date
                  Signed field is added to a document. Each field can still be
                  changed individually from its properties.
                </Typography>
              </Grid>
              <Grid item xs={12} mt={1}>
                <InputLabel id="date-format-label" sx={{ mb: 0.5 }}>
                  Date format
                </InputLabel>
                <Select
                  size="small"
                  labelId="date-format-label"
                  value={localDateFormat}
                  onChange={(e) => setLocalDateFormat(e.target.value as string)}
                  sx={{ minWidth: 180, fontSize: "0.875rem" }}
                >
                  {DATE_FORMAT_OPTIONS.map((fmt) => (
                    <MenuItem
                      key={fmt}
                      value={fmt}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {fmt}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} mt={2}>
                <Button
                  id="update_date_format_btn"
                  size="small"
                  variant="contained"
                  disabled={!isDateFormatDirty || dateFormatUpdating}
                  onClick={handleDateFormatUpdate}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default MySignRequestsConfigurations;
